<template>
  <tr
    class="clickable"
    @click="$emit('associate-equipment-packs', yachtModel)"
  >
    <td
      colspan="10"
      class="pa-0"
      :style="{ borderLeft: `5px solid ${yachtModel.status === yachtModelStatuses.STATUS_DRAFT ? '#9e9e9e' : 'transparent'}`}"
    >
      <v-row
        no-gutters
        align="center"
      >
        <v-col
          style="flex: 0 0 80px"
          class="py-3 text-center"
        >
          <v-avatar
            color="grey lighten-3"
            size="55px"
          >
            <v-img
              v-if="yachtModel.main_image"
              :src="yachtModel.main_image.path"
              alt="Main image"
            />
          </v-avatar>
          <div class="grey--text text--lighten-1 mt-1 caption">
            #{{ yachtModel.id }}
          </div>
        </v-col>
        <v-col class="py-3 pl-5">
          <v-row no-gutters>
            <v-col style="min-width: 0">
              <div class="font-weight-bold body-2 text-truncate">
                {{ yachtModel.label || yachtModel.name }}
              </div>
            </v-col>
            <v-col class="shrink pr-4">
              <organisation-chip :organisation="yachtModel.organisation" />
            </v-col>
            <v-col
              v-if="yachtModel.status === yachtModelStatuses.STATUS_DRAFT"
              class="shrink"
            >
              <div
                class="px-1 grey white--text radius-5 caption"
              >
                Draft
              </div>
            </v-col>
          </v-row>
          <div class="grey--text text--lighten-1 text-truncate">
            {{ yachtModel.manufacturer.name }} &middot; {{ yachtModel.type.label }}
          </div>
          <div class="grey--text text--lighten-1 text-truncate">
            <strong class="grey--text">
              {{ yachtModel.cabin_count_description }}
            </strong> cabins
            &middot;
            <strong class="grey--text">
              {{ yachtModel.berth_count_description }}
            </strong> RNOG
          </div>
          <div
            v-if="yachtModel.engines_description"
            class="grey--text text--lighten-1 text-truncate"
          >
            {{ yachtModel.engines_description }}
          </div>
          <div
            v-else
            class="error--text text--lighten-1"
          >
            Engines not set
          </div>
        </v-col>
        <v-col
          class="font-weight-bold"
          style="flex: 0 0 80px"
        >
          {{ yachtModel.model_year }}
        </v-col>
        <v-col
          style="flex: 0 0 70px"
          class="text-center grey--text text--lighten-1"
          :class="{ 'font-weight-bold text--darken-1': yachtModel.yachts_count > 0 }"
        >
          {{ yachtModel.prospect_yachts_count }}
        </v-col>
        <v-col
          style="flex: 0 0 50px"
          class="text-center grey--text text--lighten-1"
          :class="{ 'font-weight-bold text--darken-1': yachtModel.sold_yachts_count > 0 }"
        >
          {{ yachtModel.sold_yachts_count }}
        </v-col>
        <v-col
          style="flex: 0 0 50px"
          class="text-center grey--text text--lighten-1"
          :class="{ 'font-weight-bold text--darken-1': yachtModel.live_yachts_count > 0 }"
        >
          {{ yachtModel.live_yachts_count }}
        </v-col>
        <v-col
          v-if="$can('view-sale-price', 'YachtModel')"
          style="flex: 0 0 120px"
          class="text-right pr-4 primary--text"
        >
          {{ yachtModel.currency }}
          <strong class="ml-1">
            {{ yachtModel.sale_price | currency('') }}
          </strong>
        </v-col>
        <v-col
          cols="auto"
        >
          <v-menu>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                small
                v-on="on"
                @click.stop.prevent
              >
                <v-icon small>
                  more_vert
                </v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                @click.prevent="$emit('edit', yachtModel)"
              >
                <v-list-item-title>
                  <v-icon
                    small
                    class="mr-2"
                  >
                    edit
                  </v-icon>
                  Edit
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                @click.prevent="$emit('associate-equipment-packs', yachtModel)"
              >
                <v-list-item-title>
                  <v-icon
                    small
                    class="mr-2"
                  >
                    view_quilt
                  </v-icon>
                  Associate Equipment Packs
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                @click.prevent="$emit('copy', yachtModel.id)"
              >
                <v-list-item-title>
                  <v-icon
                    small
                    class="mr-2"
                  >
                    file_copy
                  </v-icon>
                  Copy
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <div
        class="pb-3"
        style="padding-left: 100px"
      >
        <span
          v-for="country in countries"
          :key="country.id"
          class="mr-2 v-chip theme--light v-size--small"
          :class="{ 'error': country.included && country.yachtModel.pivot.selected_equipment_categories_count < requiredYachtEquipmentPackCategoriesCount }"
        >
          <span class="v-chip__content">
            <flag
              :iso="country.code"
              :title="country.name"
              :squared="false"
              class="radius-3 mr-1 elevation-5"
              :style="[ ...!country.included && { opacity: 0.2, filter: 'grayscale(100%)' } ]"
            />
            <span v-if="country.yachtModel">
              {{ yachtModel.currency }} {{ country.yachtModel.pivot.total_sale_price | currency('') }}
            </span>
          </span>
        </span>
      </div>
    </td>
  </tr>
</template>

<script>
import { sortBy } from 'lodash'
import YachtModel from '@/models/YachtModel'
import OrganisationChip from '@/components/helpers/OrganisationChip.vue'

export default {
    name: 'YachtModelRow',
  components: { OrganisationChip },
    props: {
      yachtModel: {
        type: Object,
        required: true
      }
    },
    computed: {
      countries() {
        return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name').map(c => {
          let yachtModelCountry = this.yachtModel.countries.find(pc => c.id === pc.id)

          return {
            ...c,
            included: !!yachtModelCountry,
            yachtModel: yachtModelCountry
          }
        })
      },
      requiredYachtEquipmentPackCategoriesCount: function () {
        return this.$store.state.settings.equipmentPackCategories.data.filter(c => c.required).length
      }
    },
    created() {
      this.yachtModelStatuses = YachtModel
    }
  }
</script>
